import { inject, Injectable } from '@angular/core';
import { CurrentStateService } from '@ddv/behaviors';
import { initialDashboardPreferences, QueryParamsService } from '@ddv/filters';
import { ManagerService } from '@ddv/layout';
import {
    ADDITIONAL_FILTERS_WITH_NO_VALUES_MESSAGE,
    DashboardPreference,
    DatasetFetchKey,
    datasetFetchKeysMatch,
    FilterPreference,
    FilterQueryParams,
    QueryParams,
    SpecialCaseQueryParams,
    toFilterQueryParams,
    TREBEK_QUERY_ERROR_MESSAGE,
    WIDGET_LIFECYCLE_EVENT,
} from '@ddv/models';
import { deepCompare } from '@ddv/utils';
import { Observable } from 'rxjs';

import { DatasetDatasetFetcherService } from './dataset-dataset-fetcher.service';
import { DatasetLoadState } from './dataset-load-state';
import { DatasetQueryParamsDifferService } from './dataset-query-params-differ.service';
import { DatasetStateManagementService } from './dataset-state-management.service';
import { WidgetDataSourceService } from './widget-datasource.service';
import { DataLoad } from '../models/data-load';
import { Datasource, DataWrapper } from '../models/data-source';
import { PublicApiResponseRow } from '../models/trebek';

@Injectable()
export class DatasetManagerService {
    private readonly managerService = inject(ManagerService);
    private readonly widgetDataSourceService = inject(WidgetDataSourceService);
    private readonly queryParamsService = inject(QueryParamsService);
    private readonly datasetFetcher = inject(DatasetDatasetFetcherService);
    private readonly queryParamsDiffer = inject(DatasetQueryParamsDifferService);
    private readonly stateManager = inject(DatasetStateManagementService);
    private readonly currentStateService = inject(CurrentStateService);

    private sourceDataCopy: PublicApiResponseRow[] | undefined;
    private dashboardPreferences: DashboardPreference = initialDashboardPreferences;
    private dashboardQueryParams: FilterQueryParams | undefined;
    private readonly widgetFilterParam: Map<number, FilterQueryParams> = new Map();
    private widgetQueryParams: FilterPreference | undefined;
    private isMultiClient = false;
    private isDetailWidgetOpened = false;

    constructor() {
        this.widgetDataSourceService.dataSource$.subscribe({
            next: (datasource) => {
                this.onWidgetDataSourceChanged(datasource);
            },
        });

        this.currentStateService.isMultiClient$
            .subscribe({
                next: (isMultiClient) => this.isMultiClient = isMultiClient,
            });

        this.queryParamsService.dashboardQueryParams.subscribe({
            next: (dashboardPref) => this.onDashboardQueryParamsChanged(dashboardPref),
        });

        this.queryParamsService.widgetQueryParams.subscribe({
            next: (params) => {
                if (params?.lastChangedWidgetId) {
                    this.onWidgetQueryParamsChanged(params.lastChangedWidgetId, params.widgetFilters.get(params.lastChangedWidgetId));
                }
            },
        });

        this.managerService.isDetailWidgetOpened.subscribe({
            next: (isOpened) => this.isDetailWidgetOpened = isOpened,
        });
    }

    // this is only called when the user switches dashboards in view mode
    clearDashboardPreference(): void {
        this.dashboardPreferences = initialDashboardPreferences;
        this.dashboardQueryParams = undefined;
    }

    getUniqueKey(
        clientCode: string,
        dashboardId: string | number,
        widgetId: number,
        namedQueryId: string,
        isSubscribedToDashboardFilters: boolean | undefined,
    ): DatasetFetchKey {
        const state = this.stateManager.getState(widgetId);
        if (state) {
            return state.key;
        }

        let key = this.findSharedKey(namedQueryId, isSubscribedToDashboardFilters);
        key ??= {
            namedQueryId,
            dashboardId,
            sourceType: isSubscribedToDashboardFilters ? 'dashboard' : 'widget',
            sourceId: isSubscribedToDashboardFilters ? namedQueryId : widgetId,
        };

        this.stateManager.createState(widgetId, clientCode, key);
        return key;
    }

    // this method looks super dodgy.  how could it have only a single set of rows?  this class loads many row-sets
    getSourceDataCopy(): PublicApiResponseRow[] | undefined {
        return this.sourceDataCopy;
    }

    getDataset(
        clientCode: string,
        dashboardId: string | number,
        widgetId: number, // i dont think this is the widgetId.  i think it's actually the widgetPreferencesId which is something else
        namedQueryId: string,
        isSubscribedToDashboardFilters: boolean | undefined,
    ): Observable<DataLoad> {
        const state = this.getOrCreateState(clientCode, dashboardId, widgetId, namedQueryId, isSubscribedToDashboardFilters);

        // this is unfortunately still necessary
        // given the way DSM has evolved over time, there are many race conditions between:
        //  - the widget query params changing
        //  - the dashboard query params changing
        //  - SOMETHING in the parent widget changing and it deciding to call getDataset again
        // when realtime/refresh was added a timer pipe was used.  that does not emit for the first time until "the next tick"
        // so without this set timeout, some of the race conditions finish in a different order and that causes problems
        setTimeout(() => {
            if (!isSubscribedToDashboardFilters) {
                return;
            }

            if (state.hasData()) {
                this.emitExistingData(state);
            } else if (!state.isFetching()) {
                state.markAsLoading();

                this.fetchData(
                    namedQueryId,
                    this.dashboardPreferences,
                    this.dashboardQueryParams,
                    state.key,
                    clientCode,
                    this.isMultiClient);
            }

            if (state.isFetchingCanceled()) {
                this.managerService.sendMessageToExistingWidget(widgetId, WIDGET_LIFECYCLE_EVENT.DATA_LOADING_CANCELLED);
            } else if (state.isLoading()) {
                this.managerService.sendMessageToExistingWidget(widgetId, WIDGET_LIFECYCLE_EVENT.LOADING_DATA);
            }
        });

        return state.fetchDataObservable();
    }

    refreshDataset(key: DatasetFetchKey, markDatasetAsLoading = false): void {
        const state = this.stateManager.getState(key);
        if (!state) {
            return;
        }

        this.refreshDatasetForState(state, markDatasetAsLoading);
    }

    loadNoData(
        clientCode: string,
        dashboardId: string | number,
        widgetId: number | undefined,
        namedQueryId: string | undefined,
        isSubscribedToDashboardFilters: boolean | undefined,
    ): void {
        if (!widgetId || !namedQueryId) {
            return;
        }

        const uniqueKey = this.getUniqueKey(clientCode, dashboardId, widgetId, namedQueryId, isSubscribedToDashboardFilters);
        setTimeout(() => {
            const dataWrapper = { uniqueKey, data: [] };
            this.widgetDataSourceService.addDataSource(dataWrapper);
        }, 0);
    }

    removeDataset(widgetId: number): void {
        const state = this.stateManager.getState(widgetId);
        if (!state) {
            return;
        }

        this.cancelRequest(state.key);
        this.stateManager.allStatesForKey(state.key).forEach((s) => s.clearFetchedData());
        this.widgetDataSourceService.removeDataSource(state.key);

        this.stateManager.removeState(widgetId);
        this.widgetFilterParam.delete(widgetId);
        this.queryParamsService.removeWidgetQueryParam(widgetId);
    }

    private findSharedKey(
        namedQueryId: string | number,
        isSubscribedToDashboardFilters: boolean | undefined,
    ): DatasetFetchKey | undefined {
        if (!isSubscribedToDashboardFilters) {
            return;
        }

        return this.stateManager.allSharedStatesForNamedQuery(namedQueryId)[0]?.key;
    }

    private onDashboardQueryParamsChanged(dashboardPreferences: DashboardPreference): void {
        this.dashboardPreferences = dashboardPreferences;
        const currentQueryParam = toFilterQueryParams(dashboardPreferences);

        const paramsChanged = !this.queryParamsDiffer.areQueryParamsEqual(this.dashboardQueryParams, currentQueryParam);
        const isExplicitRefresh = dashboardPreferences.isPreferenceChangedOnRefresh ||
            currentQueryParam.acknowledged !== this.dashboardQueryParams?.acknowledged;

        if (paramsChanged) {
            this.fetchDataForUpdatedDashboardQueryParams(isExplicitRefresh);
        } else if (!deepCompare(this.dashboardQueryParams, currentQueryParam)) {
            this.dashboardQueryParams = toFilterQueryParams(dashboardPreferences);
            this.triggerDataUpdateEvent(dashboardPreferences);
        }
    }

    private fetchDataForUpdatedDashboardQueryParams(isExplicitRefresh = false): void {
        delete this.dashboardPreferences.isPreferenceChangedOnRefresh;
        this.dashboardQueryParams = toFilterQueryParams(this.dashboardPreferences);

        this.stateManager.allStatesForDashboards()
            .filter((state) => !this.isDetailWidgetOpened || isExplicitRefresh ||
                !!this.managerService.getWidgetById(state.widgetId)?.isDetailWidget)
            .forEach((state) => {
                this.managerService.sendMessageToExistingWidget(state.widgetId, WIDGET_LIFECYCLE_EVENT.LOADING_DATA);
                this.refreshDatasetForState(state, false);
            });
    }

    private onWidgetQueryParamsChanged(widgetId: number, widgetQueryParams: FilterPreference | undefined): void {
        this.widgetQueryParams = widgetQueryParams;
        const widgetPreferences = this.managerService.getWidgetPreferences(widgetId);
        // this is a strange check.  i'd guess that just checking that widgetPreferences are defined would be sufficient
        // i do not think there is a pattern by which the preferences can be defined but not have an id
        if (!widgetPreferences?.id) {
            return;
        }

        const currentQueryParam = toFilterQueryParams(widgetQueryParams);
        if (this.shouldFetchNewDataOnWidgetQueryParamsChange(widgetId, currentQueryParam, widgetPreferences)) {
            this.fetchDataForUpdatedWidgetQueryParams(widgetId, currentQueryParam, widgetPreferences);
        }
    }

    private shouldFetchNewDataOnWidgetQueryParamsChange(
        widgetId: number,
        currentQueryParam: FilterQueryParams,
        widgetPreferences: Queryable | undefined,
    ): boolean {
        if (widgetPreferences?.namedQueryId && !deepCompare(this.widgetFilterParam.get(widgetId), currentQueryParam)) {
            return true;
        }

        // This check used to be this.onRealtimeUpdate
        // which was set to true anytime a unsubscribed widget was called for getDataset
        // see comment in ApplicationBaseWidget.onModeChange
        return !!widgetPreferences?.realtimeUpdates; // && !isSubscribedToDashboardFilters
    }

    private fetchDataForUpdatedWidgetQueryParams(
        widgetId: number,
        currentQueryParam: FilterQueryParams,
        widgetPreferences: Queryable,
    ): void {
        const state = this.stateManager.getState(widgetId);
        if (!state) {
            return;
        }

        if (widgetPreferences.realtimeUpdates) {
            state.markAsLoading();
        }
        this.managerService.sendMessageToExistingWidget(widgetPreferences.id ?? 0, WIDGET_LIFECYCLE_EVENT.LOADING_DATA);

        this.fetchData(
            widgetPreferences.namedQueryId,
            this.widgetQueryParams,
            this.dashboardQueryParams,
            state.key,
            widgetPreferences.clientCode ?? '', // the ?? should never be hit
            this.isMultiClient);

        this.widgetFilterParam.set(widgetId, currentQueryParam);
    }

    private cancelRequest(uniqueKey: DatasetFetchKey): void {
        this.stateManager.allStatesForKey(uniqueKey).forEach((state) => {
            state.cancelFetching();
        });
    }

    private fetchData(
        namedQueryId: string,
        queryParams: QueryParams | undefined,
        dashboardQueryParam: FilterQueryParams | undefined,
        key: DatasetFetchKey,
        clientCode: string,
        isMultiClient: boolean,
    ): void {
        this.cancelRequest(key);

        const states = this.stateManager.allStatesForKey(key);
        const fetchDataSubscription = this.datasetFetcher.fetchData(
            clientCode,
            states.map((state) => state.widgetId),
            namedQueryId,
            queryParams,
            onlySpecialCaseParams(dashboardQueryParam), // this just makes the intent and testing easier
            isMultiClient,
        ).subscribe({
            next: (results) => {
                results.forEach((result) => {
                    states.forEach((state) => state.setWidgetData(result.data));
                });

                this.updateDatasource(states[0], key);
            },
            error: (error) => {
                if (error instanceof Error && error.message === ADDITIONAL_FILTERS_WITH_NO_VALUES_MESSAGE) {
                    states.forEach((state) => state.setWidgetData([]));
                    this.updateDatasource(states[0], key);
                    states.map((state) => state.widgetId).forEach((widgetId) => {
                        this.managerService.sendMessageToExistingWidget(
                            widgetId,
                            {
                                action: WIDGET_LIFECYCLE_EVENT.ERROR_OCCURRED,
                                exception: { message: error.message },
                            });
                    });
                }
            },
        });

        this.stateManager.allStatesForKey(key).forEach((state) => state.markAsFetching(fetchDataSubscription));
    }

    private updateDatasource(
        state: DatasetLoadState,
        uniqueKey: DatasetFetchKey,
    ): void {
        this.sourceDataCopy = state.widgetData();

        this.widgetDataSourceService.addDataSource({
            uniqueKey,
            data: state?.widgetData() ?? [],
        });
    }

    private triggerDataUpdateEvent(queryParams: FilterQueryParams): void {
        this.stateManager.allStatesForDashboards().forEach((state) => {
            const widget = this.managerService.getWidgetById(state.widgetId);
            if (!widget) {
                return;
            }

            widget.lifeCycleCallBack?.(
                WIDGET_LIFECYCLE_EVENT.DATA_UPDATE,
                {
                    data: state.fetchedData(),
                    filters: queryParams,
                });
        });
    }

    private getOrCreateState(
        clientCode: string,
        dashboardId: string | number,
        widgetId: number,
        namedQueryId: string,
        isSubscribedToDashboardFilters: boolean | undefined,
    ): DatasetLoadState {
        let state = this.stateManager.getState(widgetId);
        if (!state) {
            // this forces the state to be created with the correct key
            this.getUniqueKey(clientCode, dashboardId, widgetId, namedQueryId, isSubscribedToDashboardFilters);
            state = this.stateManager.getState(widgetId) as DatasetLoadState;

            const sharedKey = this.findSharedKey(namedQueryId, isSubscribedToDashboardFilters);
            if (sharedKey) {
                const sharedState = this.stateManager.allStatesForKey(sharedKey)[0];
                sharedState.shareFetchSubscription(state);
            }

            if (!state.hasData()) {
                state.markAsLoading();
            }
        }

        return state;
    }

    private emitExistingData(state: DatasetLoadState): void {
        if (state.hasError()) {
            this.sendGenericError(state.widgetId, state.getError());
        }

        setTimeout(() => state.emitData(), 100);
    }

    private sendGenericError(widgetId: number, time: string): void {
        this.managerService.sendMessageToExistingWidget(
            widgetId,
            {
                action: WIDGET_LIFECYCLE_EVENT.ERROR_OCCURRED,
                exception: {
                    message: TREBEK_QUERY_ERROR_MESSAGE,
                    time,
                },
            });
    }

    private refreshDatasetForState(state: DatasetLoadState, markDatasetAsLoading: boolean): void {
        if (markDatasetAsLoading) {
            state.markAsLoading();
        }

        const key = state.key;
        this.fetchData(
            key.namedQueryId,
            key.sourceType === 'widget' ? this.widgetQueryParams : this.dashboardPreferences,
            this.dashboardQueryParams,
            key,
            state.clientCode,
            this.isMultiClient);
    }

    private onWidgetDataSourceChanged(datasource: Datasource): void {
        if (!datasource.lastChangedDataset) {
            return;
        }

        const dataSourceForLastKey = datasource.datasources.find((dw) => {
            return datasetFetchKeysMatch(dw.uniqueKey, datasource.lastChangedDataset);
        });
        if (!dataSourceForLastKey) {
            return;
        }

        this.emitUpdatedDataset(datasource.lastChangedDataset, dataSourceForLastKey);
    }

    private emitUpdatedDataset(key: DatasetFetchKey, dataSourceForLastKey: DataWrapper): void {
        this.stateManager.allStatesForKey(key).forEach((state) => {
            state.emitUpdatedData(dataSourceForLastKey?.data);
        });
    }
}

function onlySpecialCaseParams(queryParam: FilterQueryParams | undefined): SpecialCaseQueryParams {
    return {
        acknowledged: queryParam?.acknowledged,
        includeManuallyReleased: queryParam?.includeManuallyReleased,
    };
}

export interface Queryable {
    id?: number;
    realtimeUpdates?: boolean;
    namedQueryId: string;
    clientCode?: string;
}
